import type { SubscriptionPlan } from '../../api/viewSubscriptionPlanApiService';
import { useViewSubscriptionPlanContent } from '../../hooks/useViewSubscriptionPlanContent';

interface ViewSubscriptionPlanContentProps {
  plan: SubscriptionPlan | null;
  activeTabId: string;
}

const ViewSubscriptionPlanContent = ({
  plan,
  activeTabId,
}: ViewSubscriptionPlanContentProps) => {
  const {
    formattedDescription,
    formattedCreatedAt,
    formattedUpdatedAt,
    formattedUpdatedBy,
    formattedPrice,
    formattedDurationType,
    formattedDuration,
    formattedBillingCycle,
    formattedBilledFor,
    formattedNumberOfUsers,
    formattedNumberOfFacilities,
    formattedNumberOfQmsDocuments,
    formattedOffer,
  } = useViewSubscriptionPlanContent(plan);

  // Basic Information Tab
  if (activeTabId === 'PodstawoweInformacje') {
    return (
      <div className="flex flex-col gap-6 m-[6px]">
        <div className="flex flex-col gap-4 border-2 border-[#DCDDE0] rounded-[8px] p-4">
          <p
            className="text-[18px] font-normal text-on-surface-variant"
            aria-label="Szczegóły planu"
            data-testid="plan-details-title"
          >
            Szczegóły planu
          </p>
          <div className="flex flex-col gap-8">
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Opis"
              >
                Opis
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Opis-value"
              >
                {formattedDescription}
              </p>
            </div>
            <div
              className="grid gap-x-8 gap-y-8"
              style={{
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              }}
            >
              <div className="flex min-w-0 flex-col gap-2">
                <h2
                  className="text-bodySmall font-normal text-on-surface-variant"
                  data-testid="Data utworzenia"
                >
                  Data utworzenia
                </h2>
                <p
                  className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                  data-testid="Data utworzenia-value"
                >
                  {formattedCreatedAt}
                </p>
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <h2
                  className="text-bodySmall font-normal text-on-surface-variant"
                  data-testid="Zmodyfikowane przez"
                >
                  Zmodyfikowane przez
                </h2>
                <p
                  className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                  data-testid="Zmodyfikowane przez-value"
                >
                  {formattedUpdatedBy}
                </p>
              </div>
              <div className="flex min-w-0 flex-col gap-2">
                <h2
                  className="text-bodySmall font-normal text-on-surface-variant"
                  data-testid="Zmodyfikowane przez-data"
                >
                  Zmodyfikowane przez
                </h2>
                <p
                  className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                  data-testid="Zmodyfikowane przez-data-value"
                >
                  {formattedUpdatedAt}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pricing & Billing Tab
  if (activeTabId === 'CennikIRozliczenia') {
    return (
      <div className="flex flex-col gap-6 m-[6px]">
        {/* Duration & Billing Section */}
        <div className="flex flex-col gap-4 border-2 border-[#DCDDE0] rounded-[8px] p-4">
          <p
            className="text-[18px] font-normal text-on-surface-variant"
            aria-label="Okres i rozliczenia"
            data-testid="duration-billing-title"
          >
            Okres i rozliczenia
          </p>
          <div
            className="grid gap-x-8 gap-y-8"
            style={{
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            }}
          >
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Typ okresu"
              >
                Typ okresu
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Typ okresu-value"
              >
                {formattedDurationType}
              </p>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Okres"
              >
                Okres
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Okres-value"
              >
                {formattedDuration}
              </p>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Cykl rozliczeniowy"
              >
                Cykl rozliczeniowy
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Cykl rozliczeniowy-value"
              >
                {formattedBillingCycle}
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="flex flex-col gap-4 border-2 border-[#DCDDE0] rounded-[8px] p-4">
          <p
            className="text-[18px] font-normal text-on-surface-variant"
            aria-label="Cennik"
            data-testid="pricing-title"
          >
            Cennik
          </p>
          <div
            className="grid gap-x-8 gap-y-8"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}
          >
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Cena"
              >
                Cena
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Cena-value"
              >
                {formattedPrice ? `${formattedPrice} zł` : '—'}
              </p>
              {formattedBilledFor && (
                <p className="text-bodySmall font-normal text-on-surface-variant">
                  {formattedBilledFor}
                </p>
              )}
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Oferta"
              >
                Oferta
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Oferta-value"
              >
                {formattedOffer}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Usage Limits Tab
  if (activeTabId === 'LimityUzywania') {
    return (
      <div className="flex flex-col gap-6 m-[6px]">
        <div className="flex flex-col gap-4 border-2 border-[#DCDDE0] rounded-[8px] p-4">
          <p
            className="text-[18px] font-normal text-on-surface-variant"
            aria-label="Limity użytkowania"
            data-testid="usage-limits-title"
          >
            Limity użytkowania
          </p>
          <div
            className="grid gap-x-8 gap-y-8"
            style={{
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            }}
          >
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Liczba użytkowników"
              >
                Liczba użytkowników
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Liczba użytkowników-value"
              >
                {formattedNumberOfUsers}
              </p>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Liczba placówek"
              >
                Liczba placówek
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Liczba placówek-value"
              >
                {formattedNumberOfFacilities}
              </p>
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <h2
                className="text-bodySmall font-normal text-on-surface-variant"
                data-testid="Liczba dokumentów QMS"
              >
                Liczba dokumentów QMS
              </h2>
              <p
                className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10 align-middle"
                data-testid="Liczba dokumentów QMS-value"
              >
                {formattedNumberOfQmsDocuments}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ViewSubscriptionPlanContent;
